import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/utils';
import { type PhxIconName } from '@/shared/ui/icon';

import { DialogFooter } from './DialogFooter';
import { DialogHeader, type DialogHeaderVariant, type DialogTone } from './DialogHeader';

export type { DialogTone, DialogHeaderVariant };

/** `top` = horní okraj fixní při změně výšky obsahu (zkracuje se dolů). */
export type DialogAlign = 'center' | 'top';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Krátký popisek na stejném řádku jako titulek (nezalamuje se). */
  subtitle?: ReactNode;
  description?: ReactNode;
  icon?: PhxIconName;
  tone?: DialogTone;
  /** Accent title bar (theme primary) — e.g. Pick records modal. */
  headerVariant?: DialogHeaderVariant;
  /** Accessible label pro zavírací tlačítko hlavičky. */
  closeLabel?: string;
  /** Row under title (e.g. pick modal search). */
  subheader?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Padding těla. `false` = tělo edge-to-edge (např. plné mřížky). Default `true`. */
  bodyPadded?: boolean;
  bodyClassName?: string;
  closeOnBackdrop?: boolean;
  /** Maximum width — defaults to 'sm' (420px). */
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Vertical placement — default `center`. */
  align?: DialogAlign;
  ariaLabel?: string;
  className?: string;
  /**
   * Bezrámový režim — vykreslí jen modální plochu (portal, backdrop, Escape, focus)
   * a `children` zcela edge-to-edge, bez automatické hlavičky, paddingu těla a patičky.
   * Konzument si v `children` postaví vlastní chrome (např. bohatý průvodce). Zachovává
   * sdílený modální mechanismus místo paralelního overlay řešení.
   */
  chromeless?: boolean;
  /**
   * Přepíše z-index kořenového portálu (default `--phx-z-overlay`, tj. overlay vrstva nad okny
   * PhoenixDesktopu — stejná jako DropdownMenu/Popover/Drawer). Předej vlastní hodnotu jen ve
   * výjimečném případě, kdy dialog musí ležet jinde než na standardní overlay vrstvě.
   */
  zIndex?: number | string;
}

const WIDTHS: Record<NonNullable<DialogProps['width']>, string> = {
  xs: 'max-w-[320px]',
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[920px]',
};

/**
 * Phoenix modal dialog.  Provides a portal-rendered surface with an optional
 * tone-tinted icon header and a `--phx-surface-2` footer band.  Closes on
 * Escape, backdrop click (opt-in via `closeOnBackdrop`), or any explicit
 * action button calling `onClose`.
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  description,
  icon,
  tone = 'neutral',
  headerVariant = 'default',
  closeLabel,
  subheader,
  children,
  footer,
  bodyPadded = true,
  bodyClassName,
  closeOnBackdrop = true,
  width = 'sm',
  align = 'center',
  ariaLabel,
  className,
  chromeless = false,
  zIndex = 'var(--phx-z-overlay)',
}: DialogProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open && surfaceRef.current) {
      surfaceRef.current.focus();
    }
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : ariaLabel}
      // Sdílený overlay token `--phx-z-overlay` (stejná vrstva jako DropdownMenu/Popover) — musí být
      // NAD okny PhoenixDesktopu, jejichž z-index roste neomezeně (10 + stackZ*100). Napevno `z-9999`
      // dialog po dost focusech schovalo za okno. Lze přepsat propem `zIndex`.
      style={{ zIndex }}
      className={cn(
        'phx-dialog-root px-phx-6 py-phx-6 fixed inset-0 flex justify-center',
        align === 'top' ? 'items-start' : 'items-center'
      )}
    >
      <div
        aria-hidden
        onClick={closeOnBackdrop ? onClose : undefined}
        className="phx-dialog-backdrop bg-phx-backdrop absolute inset-0 backdrop-blur-[1px]"
      />
      <div
        ref={surfaceRef}
        tabIndex={-1}
        className={cn(
          'phx-dialog relative z-10 flex max-h-full w-full flex-col overflow-hidden',
          'bg-phx-surface text-phx-text rounded-phx-lg border-phx-border shadow-phx-lg border',
          'outline-none',
          chromeless ? undefined : WIDTHS[width],
          className
        )}
      >
        {chromeless ? (
          children
        ) : (
          <>
            <DialogHeader
              title={title}
              subtitle={subtitle}
              description={description}
              icon={icon}
              tone={tone}
              variant={headerVariant}
              onClose={onClose}
              closeLabel={closeLabel}
            />
            {subheader ? (
              <div className="border-phx-border px-phx-6 py-phx-4 shrink-0 border-b">
                {subheader}
              </div>
            ) : null}
            {children ? (
              <div
                className={cn(
                  'phx-dialog-body text-phx-md text-phx-text min-h-0 flex-1 overflow-auto',
                  bodyPadded && (subheader ? 'px-0 pb-0' : 'p-phx-6'),
                  bodyClassName
                )}
              >
                {children}
              </div>
            ) : null}
            {footer ? <DialogFooter>{footer}</DialogFooter> : null}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
